import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGeri, useDuzenlemeBitti } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { birimSecenekleri, anaBirimKarsiligi } from '@/lib/birimler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Plus, X, Search, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import VoiceInput from '@/components/VoiceInput';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Yeni talep VE taslak talep düzenleme.
 *
 * Aynı ekran iki iş görüyor: `/purchase-requests/new` yeni kayıt açar,
 * `/purchase-requests/:id/edit` mevcut taslağı yükleyip günceller. Ayrı bir
 * düzenleme ekranı yazmak, ürün seçimi/öneri/sesli giriş mantığını ikinci kez
 * kopyalamak demekti.
 */
export default function PurchaseRequestNew() {
  const { products } = useProducts();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/purchasing');
  const duzenlemeBitti = useDuzenlemeBitti();
  const { id } = useParams();
  const duzenleme = Boolean(id);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [units, setUnits] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(Boolean(id));
  const [showVoice, setShowVoice] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [form, setForm] = useState({ cost_center_id: '', warehouse_id: '', notes: '', items: [] });
  const [openProductIndex, setOpenProductIndex] = useState(null);

  // Sadece hammadde (raw_material) ürünleri filtrele
  const rawMaterials = useMemo(() => {
    return products.filter(p => p.type === 'raw_material' || p.product_type === 'raw_material');
  }, [products]);

  useEffect(() => {
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
    api.get('/units').then(r => setUnits(r.data || [])).catch(() => {});
    api.get('/cost-centers').then(r => setCostCenters(r.data)).catch(() => {});
    // Kritik ve eksi stoklu ürünleri getir
    api.get('/products/suggested-for-purchase').then(r => setSuggestedProducts(r.data || [])).catch(() => {});
  }, []);

  // Düzenleme: mevcut talebi yükle. Taslak değilse ekranda kalmanın anlamı yok,
  // backend zaten reddeder — kullanıcıyı detaya geri gönderiyoruz.
  useEffect(() => {
    if (!id) return;
    api.get(`/purchase-requests/${id}`)
      .then(r => {
        const t = r.data;
        // Onaylanmamış talep düzenlenebilir; onaylanan/siparişe dönen kilitli
        if (!['draft', 'pending_cost_control', 'pending_manager', 'rejected'].includes(t.status)) {
          toast.error('Onaylanmış veya siparişe dönüşmüş talep düzenlenemez');
          navigate(`/erp/purchase-requests/${id}`, { replace: true });
          return;
        }
        setForm({
          cost_center_id: t.cost_center_id || '',
          warehouse_id: t.warehouse_id || '',
          notes: t.notes || '',
          items: (t.items || []).map(i => ({
            product_card_id: i.product_card_id,
            requested_quantity: String(i.requested_quantity ?? ''),
            unit: i.unit || 'kg',
          })),
        });
        setShowSuggestions(false);
      })
      .catch(() => toast.error('Talep yüklenemedi'))
      .finally(() => setYukleniyor(false));
  }, [id, navigate]);

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { product_card_id: '', requested_quantity: '', unit: 'kg' }]
    }));
  };

  // Önerilen ürünü listeye ekle
  const addSuggestedProduct = (product) => {
    // Zaten eklenmişse ekleme
    if (form.items.some(i => i.product_card_id === product.id)) {
      toast.error('Bu ürün zaten listede');
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        product_card_id: product.id,
        requested_quantity: product.shortage ? String(Math.ceil(product.shortage)) : '',
        unit: product.unit || 'kg'
      }]
    }));
  };

  // Tüm önerileri ekle
  const addAllSuggestions = () => {
    const existingIds = new Set(form.items.map(i => i.product_card_id));
    const newItems = suggestedProducts
      .filter(p => !existingIds.has(p.id))
      .map(p => ({
        product_card_id: p.id,
        requested_quantity: p.shortage ? String(Math.ceil(p.shortage)) : '',
        unit: p.unit || 'kg'
      }));
    if (newItems.length === 0) {
      toast.error('Tüm öneriler zaten eklenmiş');
      return;
    }
    setForm(prev => ({ ...prev, items: [...prev.items, ...newItems] }));
    toast.success(`${newItems.length} ürün eklendi`);
  };

  const handleVoiceConfirm = (voiceItems) => {
    const newItems = voiceItems.map(i => ({
      product_card_id: i.matched_product_id,
      requested_quantity: String(i.quantity),
      unit: i.unit || 'kg'
    }));
    setForm(prev => ({ ...prev, items: [...prev.items, ...newItems] }));
    setShowVoice(false);
  };

  const removeItem = (index) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index][field] = value;
    if (field === 'product_card_id') {
      const p = products.find(x => x.id === value);
      if (p) items[index].unit = p.unit;
    }
    setForm(prev => ({ ...prev, items }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) { toast.error('En az bir urun ekleyin'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(i => ({
          product_card_id: i.product_card_id,
          requested_quantity: parseFloat(i.requested_quantity),
          unit: i.unit
        }))
      };
      if (duzenleme) {
        await api.patch(`/purchase-requests/${id}`, payload);
        toast.success('Talep güncellendi');
        duzenlemeBitti(`/erp/purchase-requests/${id}`);
      } else {
        await api.post('/purchase-requests', payload);
        toast.success('Talep olusturuldu');
        navigate('/erp/purchasing?tab=requests');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
    setLoading(false);
  };

  // Satır yüksekliği SABİT kalmalı: çevrim ipucu belirip kaybolduğunda
  // hücreler kayıyordu. İpucu satırı her zaman ayrılır, boşken görünmez;
  // satırdaki hücreler üstten hizalanır ([&>td]:align-top).
  // NOT: sınıf adı LITERAL yazılmalı — Tailwind kaynak metni tarar.
  const IPUCU = 'h-4 mt-0.5 text-[11px] leading-4 text-[#8a7e70] truncate';

  // Birim mantığı sipariş ekranıyla ortak (lib/birimler.js)
  const kalemBirimleri = (productId) =>
    birimSecenekleri(products.find(x => x.id === productId), units);
  const kalemCevrimi = (item) =>
    anaBirimKarsiligi(products.find(x => x.id === item.product_card_id), units, item.unit);

  // Kartta yazan son alış fiyatı ANA birim başınadır. Talep koli/paket gibi
  // alternatif birimdeyse tahmini tutar çarpanla büyütülmeli — yoksa
  // "3 koli" için 3 kg'lık maliyet çıkar.
  const kalemFiyati = (item) => {
    const p = products.find(x => x.id === item.product_card_id);
    const anaFiyat = p?.last_purchase_price || 0;
    const k = kalemCevrimi(item);
    return k ? anaFiyat * k.carpan : anaFiyat;
  };

  const totalEstimated = form.items.reduce((sum, item) => {
    const qty = parseFloat(item.requested_quantity) || 0;
    return sum + qty * kalemFiyati(item);
  }, 0);

  return (
    <div data-testid="purchase-request-new-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">
          {duzenleme ? 'Talebi Düzenle' : 'Yeni Satin Alma Talebi'}
        </h1>
      </div>

      {yukleniyor && (
        <p className="text-sm text-[#8a7e70] mb-4">Talep yükleniyor...</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Talep Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Sorumluluk Merkezi</Label>
              <Select value={form.cost_center_id} onValueChange={(v) => setForm(p => ({ ...p, cost_center_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="request-cc-select">
                  <SelectValue placeholder="Merkez secin" />
                </SelectTrigger>
                <SelectContent>
                  {costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Depo *</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm(p => ({ ...p, warehouse_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="request-wh-select">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Notlar</Label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="request-notes-input" />
            </div>
          </CardContent>
        </Card>

        {showVoice && (
          <VoiceInput products={rawMaterials} onConfirm={handleVoiceConfirm} />
        )}

        {/* Önerilen Ürünler - Kritik ve Eksi Stoklu */}
        {suggestedProducts.length > 0 && showSuggestions && (
          <Card className="bg-[#7a3535]/5 border-[#7a3535]/30 shadow-sm">
            <CardHeader className="border-b border-[#7a3535]/20 flex flex-row items-center justify-between py-3">
              <CardTitle className="text-base font-bold text-[#7a3535] flex items-center gap-2">
                <span className="w-2 h-2 bg-[#7a3535] rounded-full animate-pulse"></span>
                Kritik ve Eksi Stoklu Ürünler ({suggestedProducts.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button type="button" onClick={addAllSuggestions} size="sm" variant="outline"
                  className="border-[#7a3535]/30 text-[#7a3535] hover:bg-[#7a3535]/10"
                  data-testid="add-all-suggestions-btn">
                  <Plus size={14} className="mr-1" /> Tümünü Ekle
                </Button>
                <Button type="button" onClick={() => setShowSuggestions(false)} size="sm" variant="ghost"
                  className="text-[#8a7e70] hover:text-[#1a1916]">
                  <X size={14} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2">
                {suggestedProducts.map(p => {
                  const isAdded = form.items.some(i => i.product_card_id === p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => !isAdded && addSuggestedProduct(p)}
                      disabled={isAdded}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                        isAdded 
                          ? 'bg-[#3d6e4e]/10 text-[#3d6e4e] cursor-default' 
                          : p.is_negative 
                            ? 'bg-[#7a3535]/10 text-[#7a3535] hover:bg-[#7a3535]/20 cursor-pointer'
                            : 'bg-[#7a6a2e]/10 text-[#7a6a2e] hover:bg-[#7a6a2e]/20 cursor-pointer'
                      }`}
                      data-testid={`suggestion-${p.id}`}
                    >
                      {isAdded ? <Check size={12} /> : <Plus size={12} />}
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs opacity-70">
                        {p.is_negative ? `${p.total_stock?.toFixed(1)}` : `${p.total_stock?.toFixed(1)}/${p.min_stock_level}`} {p.unit}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Urunler</CardTitle>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setShowVoice(!showVoice)} size="sm"
                variant={showVoice ? "default" : "outline"}
                className={showVoice ? "bg-[#7a3535] text-white hover:bg-[#5e2929]" : "border-[#d0c4b0] text-[#3F3E39]"}
                data-testid="toggle-voice-btn">
                {showVoice ? '✕ Kapat' : '🎤 Sesli Giris'}
              </Button>
              <Button type="button" onClick={addItem} size="sm"
                className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="add-item-btn">
                <Plus size={14} className="mr-1" /> Urun Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-24">Miktar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-20">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Stok</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Son Fiyat</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.map((item, i) => {
                  const p = rawMaterials.find(x => x.id === item.product_card_id) || products.find(x => x.id === item.product_card_id);
                  const price = kalemFiyati(item);
                  const qty = parseFloat(item.requested_quantity) || 0;
                  const cevrim = kalemCevrimi(item);
                  // Yalnizca DONUSUMU OLAN urunlerde ipucu yuvasi ayrilir
                  const cevrimliUrun = kalemBirimleri(item.product_card_id).length > 1;
                  return (
                    <TableRow key={i} className="border-[#d0c4b0] [&>td]:align-top">
                      <TableCell>
                        <Popover open={openProductIndex === i} onOpenChange={(open) => setOpenProductIndex(open ? i : null)}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={openProductIndex === i}
                              className="w-full justify-between bg-white border-[#d0c4b0] text-left font-normal"
                              data-testid={`item-product-${i}`}
                            >
                              {item.product_card_id
                                ? (() => {
                                    const selected = rawMaterials.find(x => x.id === item.product_card_id);
                                    return selected ? `${selected.code} - ${selected.name}` : "Ürün seçin";
                                  })()
                                : "Ürün seçin..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[400px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Ürün ara..." className="h-9" />
                              <CommandList>
                                <CommandEmpty>Ürün bulunamadı.</CommandEmpty>
                                <CommandGroup className="max-h-[300px] overflow-auto">
                                  {rawMaterials.map((p) => (
                                    <CommandItem
                                      key={p.id}
                                      value={`${p.code} ${p.name}`}
                                      onSelect={() => {
                                        updateItem(i, 'product_card_id', p.id);
                                        setOpenProductIndex(null);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          item.product_card_id === p.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <span className="font-mono text-xs mr-2">{p.code}</span>
                                      <span className="truncate">{p.name}</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={item.requested_quantity}
                          onChange={(e) => updateItem(i, 'requested_quantity', e.target.value)}
                          className="bg-white border-[#d0c4b0]" data-testid={`item-qty-${i}`} />
                        {cevrimliUrun && (
                          <div className={IPUCU} data-testid={`item-base-${i}`}>
                            {cevrim && qty
                              ? `stok: ${+(qty * cevrim.carpan).toFixed(4)} ${cevrim.ana}`
                              : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Ürün kartında dönüşüm tanımlıysa alınış birimi seçilebilir
                          const secenekler = kalemBirimleri(item.product_card_id);
                          if (secenekler.length <= 1) {
                            // Seçim kutusuyla AYNI yüksekliği kapla
                            return (
                              <span className="flex h-9 items-center text-sm text-[#8a7e70]">
                                {item.unit || '-'}
                              </span>
                            );
                          }
                          return (
                            <select
                              value={item.unit || ''}
                              onChange={(e) => updateItem(i, 'unit', e.target.value)}
                              title="Ürün kartında tanımlı birimlerden seçin"
                              className="h-9 w-full min-w-[92px] px-1.5 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none focus:border-[#3d6e4e]"
                              data-testid={`item-unit-${i}`}>
                              {secenekler.map(o => (
                                <option key={o.deger} value={o.deger}>{o.etiket}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-sm text-[#3F3E39]">
                        {/* Stok DAİMA ana birimde tutulur; satır alternatif
                            birimdeyse SEÇİLİ birime çevrilerek gösterilir,
                            ana birim karşılığı altta ipucu olarak durur. */}
                        <div className="flex h-9 items-center justify-end">
                          {p?.total_stock == null ? '-' : (
                            cevrim
                              ? `${+(p.total_stock / cevrim.carpan).toFixed(1)} ${item.unit}`
                              : `${p.total_stock.toFixed(1)} ${p.unit || ''}`.trim()
                          )}
                        </div>
                        {cevrimliUrun && (
                          <div className={`${IPUCU} text-right`}>
                            {cevrim && p?.total_stock != null
                              ? `${p.total_stock.toFixed(1)} ${cevrim.ana}`
                              : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#3F3E39]">
                        <div className="flex h-9 items-center justify-end">
                          {price ? `${price.toFixed(2)} TL/${item.unit || ''}` : '-'}
                        </div>
                        {cevrimliUrun && (
                          <div className={`${IPUCU} text-right`}>
                            {cevrim && price
                              ? `${+(price / cevrim.carpan).toFixed(4)} TL/${cevrim.ana}`
                              : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-[#1a1916]">
                        <div className="flex h-9 items-center justify-end">{(qty * price).toFixed(2)} TL</div>
                      </TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10" data-testid={`remove-item-${i}`}>
                          <X size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {form.items.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#8a7e70]">Urun ekleyin</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
          <p className="text-sm text-[#8a7e70]">
            Tahmini Toplam: <span className="font-bold text-[#1a1916] text-lg">{totalEstimated.toFixed(2)} TL</span>
          </p>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]" data-testid="cancel-btn">Iptal</Button>
            <Button type="submit" disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="save-request-btn">
              <Save size={16} className="mr-2" />
              {loading ? 'Kaydediliyor...' : (duzenleme ? 'Güncelle' : 'Kaydet')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
